import { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import { AnimatePresence, motion } from "framer-motion";
import { SurfSpotCard } from "@/components/ui/surf-spot-card";
import { GlobeDestinationPreview } from "@/components/ui/globe-destination-preview";

interface SurfSpot {
  id: string;
  name: string;
  lat: number;
  lng: number;
  summary: string;
  image?: string;
  country: string;
  skillLevel?: string;
  skillLabel?: string;
  waveType?: string;
  crowdLevel?: string;
  bestMonths?: string[];
  costRating?: number;
  consistency?: number;
  vibe?: string[];
}

interface RotatingEarthProps {
  width?: number;
  height?: number;
  className?: string;
  surfSpots: SurfSpot[];
  focusCountry?: string;
  onSpotClick?: (spot: SurfSpot) => void;
}

// Brand accent (terracotta) — kept in sync with --primary in index.css
const PIN_CORE = "hsl(15, 55%, 50%)";
const PIN_CORE_HOVER = "hsl(15, 60%, 58%)";
const PIN_GLOW_MID = "rgba(198, 93, 58, 0.5)";
const PIN_GLOW_MID_SOFT = "rgba(198, 93, 58, 0.32)";
const PIN_GLOW_OUTER = "rgba(198, 93, 58, 0.22)";
const PIN_GLOW_OUTER_SOFT = "rgba(198, 93, 58, 0.13)";

const MIN_SCALE = 0.5;
const MAX_SCALE = 4.5;
const TILT_CLAMP = 65; // narrower than ±90 so poles aren't reachable
const CLICK_MOVE_THRESHOLD = 7; // px total movement below which a pointerup counts as a tap

// Rightside reserve so zoom controls never overlap the sphere or clip the viewport.
const CONTROL_RESERVE = 56;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Natural Earth GeoJSON is untyped third-party data
type LandFeatures = any;

export default function RotatingEarth({
  width = 800,
  height = 600,
  className = "",
  surfSpots = [],
  focusCountry,
  onSpotClick,
}: RotatingEarthProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeSpot, setActiveSpot] = useState<SurfSpot | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isMobileView, setIsMobileView] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Mutable refs
  const projectionRef = useRef<d3.GeoProjection | null>(null);
  const rotationRef = useRef<[number, number]>([0, -15]);
  const scaleRef = useRef<number>(1);
  const baseRadiusRef = useRef<number>(0);
  const landFeaturesRef = useRef<LandFeatures>(null);
  const onSpotClickRef = useRef(onSpotClick);
  const activeSpotRef = useRef<SurfSpot | null>(null);
  const isLockedRef = useRef<boolean>(false); // tap/click-locked selection
  const surfSpotsRef = useRef(surfSpots);
  const renderRef = useRef<(() => void) | null>(null);
  const zoomAnimRef = useRef<number | null>(null);

  useEffect(() => {
    onSpotClickRef.current = onSpotClick;
  }, [onSpotClick]);

  useEffect(() => {
    activeSpotRef.current = activeSpot;
    if (renderRef.current) renderRef.current();
  }, [activeSpot]);

  useEffect(() => {
    surfSpotsRef.current = surfSpots;
  }, [surfSpots]);

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const checkHitSpot = useCallback((x: number, y: number, canvasWidth: number, canvasHeight: number) => {
    const projection = projectionRef.current;
    if (!projection) return null;
    const center = projection.invert!([canvasWidth / 2, canvasHeight / 2]);
    if (!center) return null;
    let closest: SurfSpot | null = null;
    let closestDist = Infinity;
    const DETECTION_RADIUS = 32;
    for (const spot of surfSpotsRef.current) {
      const coords: [number, number] = [spot.lng, spot.lat];
      if (d3.geoDistance(coords, center) >= Math.PI / 2) continue;
      const p = projection(coords);
      if (!p) continue;
      const d = Math.hypot(p[0] - x, p[1] - y);
      if (d < DETECTION_RADIUS && d < closestDist) {
        closestDist = d;
        closest = spot;
      }
    }
    return closest;
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    if (!context) return;

    const isMobile = window.innerWidth < 768;
    // Leave room on the right for the zoom control column so it can never overlap
    // the sphere or be clipped by narrow viewports.
    const rightReserve = isMobile ? 44 : CONTROL_RESERVE;
    const rawWidth = Math.min(width, window.innerWidth - 32);
    const canvasWidth = Math.max(240, rawWidth - rightReserve);
    const canvasHeight = Math.min(height, window.innerHeight - 120);
    const baseRadius = Math.min(canvasWidth, canvasHeight) / (isMobile ? 2.1 : 2.5);
    baseRadiusRef.current = baseRadius;
    const radius = baseRadius * scaleRef.current;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;
    context.scale(dpr, dpr);

    const projection = d3
      .geoOrthographic()
      .scale(radius)
      .translate([canvasWidth / 2, canvasHeight / 2])
      .clipAngle(90);
    projectionRef.current = projection;

    const path = d3.geoPath().projection(projection).context(context);

    let rotation = rotationRef.current;
    let autoRotate = true;
    let isDragging = false;

    // ---- Inertia state ----
    let inertiaVX = 0; // deg per second on longitude
    let inertiaVY = 0; // deg per second on latitude
    const INERTIA_FRICTION = 2.2; // higher = settles faster
    const INERTIA_MIN = 3; // deg/sec below which inertia stops

    // ---- Pointer tracking ----
    interface PointerSample { x: number; y: number; t: number }
    let pointerSamples: PointerSample[] = [];
    let downX = 0;
    let downY = 0;
    let totalMove = 0;
    let startRotationSnap: [number, number] = [0, 0];

    const findCenterSpot = (): SurfSpot | null => {
      const proj = projectionRef.current;
      if (!proj) return null;
      const cx = canvasWidth / 2;
      const cy = canvasHeight / 2;
      const center = proj.invert!([cx, cy]);
      if (!center) return null;
      let best: SurfSpot | null = null;
      let bestDist = Infinity;
      for (const spot of surfSpotsRef.current) {
        const coords: [number, number] = [spot.lng, spot.lat];
        if (d3.geoDistance(coords, center) >= Math.PI / 2.5) continue;
        const p = proj(coords);
        if (!p) continue;
        const d = Math.hypot(p[0] - cx, p[1] - cy);
        if (d < bestDist) {
          bestDist = d;
          best = spot;
        }
      }
      return best;
    };

    // Nearest spots for labelling (2 closest to center on visible face)
    const findNearestForLabels = (): Array<{ spot: SurfSpot; angDist: number; x: number; y: number }> => {
      const proj = projectionRef.current;
      if (!proj) return [];
      const cx = canvasWidth / 2;
      const cy = canvasHeight / 2;
      const center = proj.invert!([cx, cy]);
      if (!center) return [];
      const list: Array<{ spot: SurfSpot; angDist: number; x: number; y: number }> = [];
      for (const spot of surfSpotsRef.current) {
        const coords: [number, number] = [spot.lng, spot.lat];
        const ang = d3.geoDistance(coords, center);
        if (ang >= Math.PI / 3) continue;
        const p = proj(coords);
        if (!p) continue;
        list.push({ spot, angDist: ang, x: p[0], y: p[1] });
      }
      list.sort((a, b) => a.angDist - b.angDist);
      return list.slice(0, 2);
    };

    let lastAutoSelectId: string | null = null;
    const updateAutoSelected = () => {
      if (!isMobile) return;
      if (isLockedRef.current) return; // user has explicitly tapped a pin
      const spot = findCenterSpot();
      if (spot?.id !== lastAutoSelectId) {
        lastAutoSelectId = spot?.id || null;
        setActiveSpot(spot);
      }
    };

    const render = () => {
      context.clearRect(0, 0, canvasWidth, canvasHeight);
      const cx = canvasWidth / 2;
      const cy = canvasHeight / 2;
      const r = projection.scale();
      const scaleFactor = r / radius;

      // Ocean sphere — slightly richer gradient
      context.beginPath();
      context.arc(cx, cy, r, 0, 2 * Math.PI);
      const oceanGrad = context.createRadialGradient(cx - r * 0.25, cy - r * 0.3, r * 0.1, cx, cy, r);
      oceanGrad.addColorStop(0, "hsl(171, 19%, 41%)");
      oceanGrad.addColorStop(0.55, "hsl(176, 32%, 26%)");
      oceanGrad.addColorStop(1, "hsl(178, 45%, 12%)");
      context.fillStyle = oceanGrad;
      context.fill();

      // Land
      const landFeatures = landFeaturesRef.current;
      if (landFeatures) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped Natural Earth GeoJSON
        landFeatures.features.forEach((feature: any) => {
          context.beginPath();
          path(feature);
          context.fillStyle = "hsl(40, 45%, 91%)";
          context.fill();
          context.strokeStyle = "hsl(37, 27%, 70%)";
          context.lineWidth = 0.8 * scaleFactor;
          context.stroke();
        });
      }

      // Rim light / fresnel — subtle premium edge highlight
      const rimGrad = context.createRadialGradient(cx, cy, r * 0.86, cx, cy, r);
      rimGrad.addColorStop(0, "rgba(255, 255, 255, 0)");
      rimGrad.addColorStop(0.7, "rgba(242, 235, 221, 0.12)");
      rimGrad.addColorStop(0.95, "rgba(242, 235, 221, 0.28)");
      rimGrad.addColorStop(1, "rgba(242, 235, 221, 0.05)");
      context.beginPath();
      context.arc(cx, cy, r, 0, 2 * Math.PI);
      context.fillStyle = rimGrad;
      context.fill();

      // Pins
      const currentActive = activeSpotRef.current;
      surfSpotsRef.current.forEach((spot) => {
        const projected = projection([spot.lng, spot.lat]);
        if (!projected) return;
        const [x, y] = projected;
        const coords: [number, number] = [spot.lng, spot.lat];
        const center = projection.invert!([cx, cy]);
        if (!center) return;
        const distance = d3.geoDistance(coords, center);
        if (distance >= Math.PI / 2) return;

        const isActive = currentActive?.id === spot.id;
        // Outer halo
        context.beginPath();
        context.arc(x, y, isActive ? 22 : 15, 0, 2 * Math.PI);
        context.fillStyle = isActive ? PIN_GLOW_OUTER : PIN_GLOW_OUTER_SOFT;
        context.fill();
        // Middle glow
        context.beginPath();
        context.arc(x, y, isActive ? 13 : 9, 0, 2 * Math.PI);
        context.fillStyle = isActive ? PIN_GLOW_MID : PIN_GLOW_MID_SOFT;
        context.fill();
        // Core
        context.beginPath();
        context.arc(x, y, isActive ? 7 : 5.5, 0, 2 * Math.PI);
        context.fillStyle = isActive ? PIN_CORE_HOVER : PIN_CORE;
        context.fill();
        context.strokeStyle = "rgba(255,255,255,0.95)";
        context.lineWidth = isActive ? 2.2 : 1.6;
        context.stroke();
      });

      // Restrained labels for the 1–2 pins nearest the current center.
      // Collision-aware: if the second label's box would overlap the first,
      // drop it so the single "closest match" label reads cleanly.
      const labelCandidates = findNearestForLabels();
      context.font = "500 11px ui-sans-serif, system-ui, -apple-system, sans-serif";
      const padX = 6;
      const padY = 3;
      const boxH = 16;
      const COLLISION_GAP = 4;
      type LabelBox = { boxX: number; boxY: number; boxW: number; boxH: number };
      const placed: LabelBox[] = [];
      const boxesOverlap = (a: LabelBox, b: LabelBox) =>
        a.boxX < b.boxX + b.boxW + COLLISION_GAP &&
        a.boxX + a.boxW + COLLISION_GAP > b.boxX &&
        a.boxY < b.boxY + b.boxH + COLLISION_GAP &&
        a.boxY + a.boxH + COLLISION_GAP > b.boxY;

      for (const { spot, angDist, x, y } of labelCandidates) {
        const opacity = Math.max(0, 1 - angDist / (Math.PI / 3.2));
        if (opacity < 0.35) continue;
        const label = spot.name;
        const metrics = context.measureText(label);
        const textX = x + 12;
        const textY = y - 10;
        const boxX = textX - padX;
        const boxY = textY - boxH / 2;
        const boxW = metrics.width + padX * 2;
        const box: LabelBox = { boxX, boxY, boxW, boxH };
        // Prefer the nearest (candidates are pre-sorted). Skip any subsequent
        // that would overlap so the closest-match label always wins cleanly.
        if (placed.some((p) => boxesOverlap(p, box))) continue;
        placed.push(box);

        context.save();
        context.globalAlpha = opacity * 0.95;
        context.textAlign = "left";
        context.textBaseline = "middle";
        context.fillStyle = "rgba(32, 37, 34, 0.72)";
        context.beginPath();
        const rr = 4;
        context.moveTo(boxX + rr, boxY);
        context.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + boxH, rr);
        context.arcTo(boxX + boxW, boxY + boxH, boxX, boxY + boxH, rr);
        context.arcTo(boxX, boxY + boxH, boxX, boxY, rr);
        context.arcTo(boxX, boxY, boxX + boxW, boxY, rr);
        context.closePath();
        context.fill();
        context.fillStyle = "rgba(242, 235, 221, 1)";
        context.fillText(label, textX, textY + padY - 1);
        context.restore();
      }

    };
    renderRef.current = render;

    // ---- Ticker ----
    let lastElapsed = 0;
    let lastAutoCheck = 0;
    const rotate = (elapsed: number) => {
      const dt = lastElapsed === 0 ? 0 : (elapsed - lastElapsed) / 1000;
      lastElapsed = elapsed;

      // Rotation must freeze on the same lock signal that freezes selection
      // for each platform (isLockedRef on mobile, activeSpotRef on desktop)
      // -- previously mobile's tap-lock had no rotation-freeze counterpart,
      // so the globe kept auto-rotating underneath a locked card.
      const rotationLocked = isMobile ? isLockedRef.current : activeSpotRef.current !== null;
      const hasInertia = Math.abs(inertiaVX) > INERTIA_MIN || Math.abs(inertiaVY) > INERTIA_MIN;

      if (isDragging) {
        // pointer handler updates rotation directly
      } else if (hasInertia) {
        rotation[0] += inertiaVX * dt;
        rotation[1] = Math.max(-TILT_CLAMP, Math.min(TILT_CLAMP, rotation[1] + inertiaVY * dt));
        const decay = Math.exp(-INERTIA_FRICTION * dt);
        inertiaVX *= decay;
        inertiaVY *= decay;
        rotationRef.current = rotation;
        projection.rotate(rotation);
      } else if (autoRotate && !rotationLocked) {
        rotation[0] += 9 * dt; // ~0.15°/frame at 60fps
        rotationRef.current = rotation;
        projection.rotate(rotation);
      }

      if (isMobile && elapsed - lastAutoCheck > 150) {
        lastAutoCheck = elapsed;
        updateAutoSelected();
      }
      render();
    };
    const rotationTimer = d3.timer(rotate);

    // ---- Data loading ----
    let cancelled = false;
    const loadData = async (attempt = 0) => {
      try {
        setLoadError(null);
        const res = await fetch("/geo/countries-110m.json");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const countries = await res.json();
        if (cancelled) return;
        landFeaturesRef.current = countries;
        if (focusCountry) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped GeoJSON
          const feature = countries.features.find((f: any) =>
            f.properties.ADMIN === focusCountry || f.properties.NAME === focusCountry,
          );
          if (feature) {
            const center = d3.geoCentroid(feature);
            rotation = [-center[0], -center[1]];
            rotationRef.current = rotation;
            projection.rotate(rotation);
          }
        }
        render();
      } catch (err) {
        console.error("Failed to load country data:", err);
        if (cancelled) return;
        if (attempt < 2) {
          setTimeout(() => loadData(attempt + 1), 500 * (attempt + 1));
        } else {
          setLoadError("Could not load globe data.");
        }
      }
    };

    // ---- Interaction handlers (unified pointer events) ----
    const applyRotationFromDrag = (dx: number, dy: number) => {
      const s = 0.5 / scaleRef.current;
      rotation = [startRotationSnap[0] + dx * s, startRotationSnap[1] - dy * s];
      rotation[1] = Math.max(-TILT_CLAMP, Math.min(TILT_CLAMP, rotation[1]));
      rotationRef.current = rotation;
      projection.rotate(rotation);
    };

    const onPointerDown = (e: PointerEvent) => {
      isLockedRef.current = false;
      // Ignore right/middle mouse buttons; ignore secondary touches
      if (e.pointerType === "mouse" && e.button !== 0) return;
      canvas.setPointerCapture(e.pointerId);
      isDragging = true;
      autoRotate = false;
      inertiaVX = 0;
      inertiaVY = 0;
      downX = e.clientX;
      downY = e.clientY;
      totalMove = 0;
      pointerSamples = [{ x: e.clientX, y: e.clientY, t: performance.now() }];
      startRotationSnap = [rotation[0], rotation[1]];
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging) {
        // Hover-only preview on desktop while nothing is dragged/locked; only if no active locked spot
        if (e.pointerType === "mouse" && !isLockedRef.current) {
          const rect = canvas.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const spot = checkHitSpot(x, y, canvasWidth, canvasHeight);
          if (spot?.id !== activeSpotRef.current?.id) {
            if (spot) {
              setTooltipPos({ x, y });
              setActiveSpot(spot);
            } else if (!isLockedRef.current) {
              setActiveSpot(null);
            }
          }
        }
        return;
      }
      const dx = e.clientX - downX;
      const dy = e.clientY - downY;
      totalMove = Math.max(totalMove, Math.hypot(dx, dy));
      applyRotationFromDrag(dx, dy);

      pointerSamples.push({ x: e.clientX, y: e.clientY, t: performance.now() });
      // Keep only last ~120ms
      const cutoff = performance.now() - 120;
      while (pointerSamples.length > 2 && pointerSamples[0].t < cutoff) {
        pointerSamples.shift();
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!isDragging) return;
      isDragging = false;
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* noop */ }

      if (totalMove < CLICK_MOVE_THRESHOLD) {
        // Treat as a tap/click: select pin at position, or dismiss if empty
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const spot = checkHitSpot(x, y, canvasWidth, canvasHeight);
        if (spot) {
          isLockedRef.current = true;
          setTooltipPos({ x, y });
          setActiveSpot(spot);
        } else {
          isLockedRef.current = false;
          setActiveSpot(null);
        }
        autoRotate = true;
        return;
      }

      // Actual drag — compute velocity from recent samples for inertia
      autoRotate = true;
      if (pointerSamples.length >= 2) {
        const first = pointerSamples[0];
        const last = pointerSamples[pointerSamples.length - 1];
        const dt = (last.t - first.t) / 1000;
        if (dt > 0.01) {
          const vx = (last.x - first.x) / dt; // px/sec
          const vy = (last.y - first.y) / dt;
          const s = 0.5 / scaleRef.current; // same coef as drag
          // Cap to avoid absurd spins
          inertiaVX = Math.max(-360, Math.min(360, vx * s));
          inertiaVY = Math.max(-240, Math.min(240, -vy * s));
        }
      }
    };

    const onPointerCancel = () => {
      isDragging = false;
      autoRotate = true;
    };

    const onPointerLeave = () => {
      if (isDragging) return;
      if (!isLockedRef.current) {
        // Clear hover-only card on desktop
        if (!isMobile) setActiveSpot(null);
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = -e.deltaY * 0.001;
      scaleRef.current = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scaleRef.current + delta));
      projection.scale(baseRadiusRef.current * scaleRef.current);
      render();
    };

    // Pinch to zoom (touch)
    let lastPinchDist = 0;
    const onTouchMoveZoom = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        isDragging = false; // hand off from drag
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        if (lastPinchDist > 0) {
          const ratio = dist / lastPinchDist;
          scaleRef.current = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scaleRef.current * ratio));
          projection.scale(baseRadiusRef.current * scaleRef.current);
          render();
        }
        lastPinchDist = dist;
      }
    };
    const onTouchEndZoom = (e: TouchEvent) => {
      if (e.touches.length < 2) lastPinchDist = 0;
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerCancel);
    canvas.addEventListener("pointerleave", onPointerLeave);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("touchmove", onTouchMoveZoom, { passive: false });
    canvas.addEventListener("touchend", onTouchEndZoom);

    loadData();

    return () => {
      cancelled = true;
      rotationTimer.stop();
      renderRef.current = null;
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("touchmove", onTouchMoveZoom);
      canvas.removeEventListener("touchend", onTouchEndZoom);
    };
  }, [width, height, focusCountry, checkHitSpot]);

  // Smoothly animate scale for zoom-button clicks (~220ms ease-out).
  const applyZoom = (factor: number) => {
    const target = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scaleRef.current * factor));
    const from = scaleRef.current;
    if (from === target) return;
    if (zoomAnimRef.current !== null) cancelAnimationFrame(zoomAnimRef.current);
    const startT = performance.now();
    const dur = 220;
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const t = Math.min(1, (now - startT) / dur);
      const v = from + (target - from) * easeOut(t);
      scaleRef.current = v;
      if (projectionRef.current && baseRadiusRef.current) {
        projectionRef.current.scale(baseRadiusRef.current * v);
        renderRef.current?.();
      }
      if (t < 1) zoomAnimRef.current = requestAnimationFrame(step);
      else zoomAnimRef.current = null;
    };
    zoomAnimRef.current = requestAnimationFrame(step);
  };

  return (
    <div ref={containerRef} className="relative inline-block touch-none">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="Interactive globe showing surf destinations around the world. This is a decorative exploratory visualisation; use the 'Browse All Destinations' button below for a keyboard-accessible list."
        className={`cursor-grab active:cursor-grabbing ${className}`}
      />

      {loadError && (
        <div className="absolute inset-x-0 top-3 flex justify-center pointer-events-none">
          <div className="pointer-events-auto bg-card backdrop-blur-sm border border-foreground/25 rounded-full px-3 py-1.5 text-xs text-muted-foreground shadow">
            {loadError}{" "}
            <button
              type="button"
              className="text-primary underline underline-offset-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Zoom controls — sit in the reserved right column so they never overlap the sphere */}
      <div className="absolute top-3 right-3 z-40 flex flex-col gap-1.5">
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => applyZoom(1.25)}
          className="w-9 h-9 rounded-full bg-card backdrop-blur-sm border border-foreground/25 text-foreground shadow-md flex items-center justify-center text-lg font-semibold active:scale-95 transition"
        >
          +
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => applyZoom(1 / 1.25)}
          className="w-9 h-9 rounded-full bg-card backdrop-blur-sm border border-foreground/25 text-foreground shadow-md flex items-center justify-center text-lg font-semibold active:scale-95 transition"
        >
          −
        </button>
      </div>

      {/* Selected surf spot card (click/tap-locked or mobile auto-select).
          Do not use mode="wait" here. Mobile auto-selection can change
          activeSpot faster than the exit animation completes, causing
          AnimatePresence to display a stale card. Concurrent enter/exit
          keeps the rendered card in sync with activeSpot. */}
      <AnimatePresence>
        {activeSpot && activeSpot.skillLevel && (
          <motion.div
            key={activeSpot.id}
            // Mobile: a normal in-flow block directly beneath the globe,
            // not viewport-fixed. The earlier fixed-position overlay (docked
            // to the viewport bottom, gated by a pair of IntersectionObservers
            // so it would hide near the page's own "Browse All Destinations"
            // button) rendered correctly in every Chrome-based test but
            // never appeared on a real iPhone. `position: fixed` anchored
            // near the viewport bottom is a known weak point on iOS Safari
            // -- it can end up obscured by or computed against the browser's
            // own bottom toolbar rather than the visible area -- and no
            // static CSS review can rule that out short of a real device.
            // In-flow placement sidesteps the whole category: it can't be
            // clipped by browser chrome because it isn't fixed to the
            // viewport at all, it can't cover the button below because
            // normal document flow pushes that button down to make room,
            // and it needs no visibility-gating JS because scrolling it out
            // of view is just how normal page content already works.
            className={isMobileView ? "mt-4 w-full" : "absolute z-50 pointer-events-auto"}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={
              isMobileView
                ? undefined
                : {
                    left: Math.min(tooltipPos.x + 20, (containerRef.current?.offsetWidth || 400) - 300),
                    top: Math.max(10, tooltipPos.y - 150),
                  }
            }
          >
            {isMobileView ? (
              <GlobeDestinationPreview
                imageUrl={activeSpot.image || "https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=400"}
                name={activeSpot.name}
                country={activeSpot.country}
                skillLevel={activeSpot.skillLevel}
                skillLabel={activeSpot.skillLabel}
                onClick={() => {
                  if (onSpotClickRef.current && activeSpot) {
                    onSpotClickRef.current(activeSpot);
                  }
                }}
              />
            ) : (
              <SurfSpotCard
                imageUrl={activeSpot.image || "https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=400"}
                name={activeSpot.name}
                country={activeSpot.country}
                skillLevel={activeSpot.skillLevel}
                skillLabel={activeSpot.skillLabel}
                waveType={activeSpot.waveType || "beach"}
                crowdLevel={activeSpot.crowdLevel || "moderate"}
                costRating={activeSpot.costRating || 3}
                consistency={activeSpot.consistency || 7}
                bestMonths={activeSpot.bestMonths || ["May", "Jun"]}
                vibe={activeSpot.vibe || ["chill", "adventure"]}
                onExploreClick={() => {
                  if (onSpotClickRef.current && activeSpot) {
                    onSpotClickRef.current(activeSpot);
                  }
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fallback tooltip for spots without full card data */}
      <AnimatePresence>
        {activeSpot && !activeSpot.skillLevel && (
          <motion.div
            key={`simple-${activeSpot.id}`}
            className="absolute z-50 pointer-events-none"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.1 }}
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y - 10,
              transform: "translate(-50%, -100%)",
            }}
          >
            <div className="bg-card border border-border rounded-xl shadow-xl px-4 py-3 min-w-[180px] max-w-[220px]">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground text-sm leading-tight">{activeSpot.name}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">{activeSpot.country}</p>
                  <p className="text-xs text-primary mt-1.5 font-medium">{activeSpot.summary}</p>
                </div>
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-card border-r border-b border-border rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export type { SurfSpot };
